import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { UserService, CreateUserRequest, Client, Module } from '../../../services/user.service';
import { ControlDusService } from '../../../services/control-dus.service';
import { catchError, finalize, startWith, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TransportistaOption } from '@opb-workspace/shared';

@Component({
    selector: 'app-create-user-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatSnackBarModule,
        MatCardModule,
        MatAutocompleteModule,
    ],
    templateUrl: './create-user-page.component.html',
    styleUrl: './create-user-page.component.scss'
})
export class CreateUserPageComponent implements OnInit {
    userForm: FormGroup;
    isLoading = false;
    isLoadingClients = false;

    roles = [
        { value: 'admin', label: 'Administrador' },
        { value: 'operator', label: 'Operador' },
        { value: 'client', label: 'Cliente' },
        { value: 'consignatario', label: 'Consignatario' },
        { value: 'transportista', label: 'Transportista' }
    ];

    clients: Client[] = [];
    clientControl = new FormControl<string | Client>('');
    filteredClients!: Observable<Client[]>;

    consignees: { consigneeId: number; consigneeShortName: string }[] = [];
    isLoadingConsignees = false;

    // feat-rol-transportista: la lista se pide solo al elegir el rol (consulta SQL Server)
    transportistas: TransportistaOption[] = [];
    isLoadingTransportistas = false;
    transportistasLoadError: string | null = null;
    private transportistasLoaded = false;

    availableModules: Module[] = [];
    selectedModules: string[] = [];
    isLoadingModules = false;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private fb: FormBuilder,
        // eslint-disable-next-line no-unused-vars
        private userService: UserService,
        // eslint-disable-next-line no-unused-vars
        private controlDusService: ControlDusService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {
        this.userForm = this.fb.group({
            email: ['', [Validators.required, Validators.email]],
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
            phone: ['', [Validators.required]],
            role: ['operator', [Validators.required]],
            clientId: [null],
            consigneeId: [null],
            transportistaId: [null],
            password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]/)]],
            confirmPassword: ['', [Validators.required]],
            isActive: [true]
        }, { validators: this.passwordMatchValidator });

        // Escuchar cambios en el rol para validar cliente/consignatario
        this.userForm.get('role')?.valueChanges.subscribe(role => {
            const clientIdControl = this.userForm.get('clientId');
            const consigneeIdControl = this.userForm.get('consigneeId');
            this.applyTransportistaRole(role);
            if (role === 'client') {
                clientIdControl?.setValidators([Validators.required]);
                this.clientControl.setValidators([Validators.required]);
                consigneeIdControl?.clearValidators();
                consigneeIdControl?.setValue(null);
            } else if (role === 'consignatario') {
                consigneeIdControl?.setValidators([Validators.required]);
                clientIdControl?.clearValidators();
                clientIdControl?.setValue(null);
                this.clientControl.clearValidators();
                this.clientControl.setValue('');
            } else {
                clientIdControl?.clearValidators();
                clientIdControl?.setValue(null);
                this.clientControl.clearValidators();
                this.clientControl.setValue('');
                consigneeIdControl?.clearValidators();
                consigneeIdControl?.setValue(null);
            }
            clientIdControl?.updateValueAndValidity();
            this.clientControl.updateValueAndValidity();
            consigneeIdControl?.updateValueAndValidity();
        });
    }

    ngOnInit(): void {
        this.loadClients();
        this.loadModules();
        this.loadConsignees();

        this.filteredClients = this.clientControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filterClients(value)),
        );
    }

    private _filterClients(value: string | Client | null): Client[] {
        if (!value) {
            return this.clients.slice();
        }
        const filterValue = (typeof value === 'string' ? value : value.cliente).toLowerCase();
        return this.clients.filter(client => client.cliente.toLowerCase().includes(filterValue));
    }

    displayClient(client: Client): string {
        return client && client.cliente ? client.cliente : '';
    }

    onClientSelected(event: any): void {
        const selectedClient = event.option.value as Client;
        this.userForm.get('clientId')?.setValue(selectedClient.clienteId);
    }

    private loadClients(): void {
        this.isLoadingClients = true;
        this.userService.getActiveClients()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al cargar clientes', error as Error, 'CreateUserPageComponent');
                    this.snackBar.open('Error al cargar la lista de clientes', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoadingClients = false;
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    this.clients = response.data;
                }
            });
    }

    private loadModules(): void {
        this.isLoadingModules = true;
        this.userService.getAvailableModules()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al cargar módulos', error as Error, 'CreateUserPageComponent');
                    this.snackBar.open('Error al cargar la lista de módulos', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar']
                    });
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoadingModules = false;
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    // Filtrar solo módulos activos
                    // El backend ya filtra por módulos activos, pero por seguridad también filtramos aquí
                    // El backend ahora devuelve isActive (camelCase)
                    this.availableModules = response.data.filter(module => {
                        const isActive = (module as any).isActive !== undefined 
                            ? (module as any).isActive 
                            : (module as any).is_active !== undefined 
                            ? (module as any).is_active 
                            : true;
                        return isActive === true;
                    });
                    this.logger.debug(`Módulos filtrados: ${this.availableModules.length} activos de ${response.data.length} totales`, 'CreateUserPageComponent');
                }
            });
    }

    onModuleToggle(moduleCode: string, event: any): void {
        if (event.checked) {
            if (!this.selectedModules.includes(moduleCode)) {
                this.selectedModules.push(moduleCode);
            }
        } else {
            this.selectedModules = this.selectedModules.filter(code => code !== moduleCode);
        }
    }

    isModuleSelected(moduleCode: string): boolean {
        return this.selectedModules.includes(moduleCode);
    }

    shouldShowClientSelect(): boolean {
        return this.userForm.get('role')?.value === 'client';
    }

    shouldShowTransportistaSelect(): boolean {
        return this.userForm.get('role')?.value === 'transportista';
    }

    /** Requerido solo con rol transportista; al cambiar a otro rol se oculta y se limpia. */
    private applyTransportistaRole(role: string): void {
        const control = this.userForm.get('transportistaId');
        if (role === 'transportista') {
            control?.setValidators([Validators.required]);
            this.loadTransportistas();
        } else {
            control?.clearValidators();
            control?.setValue(null);
        }
        control?.updateValueAndValidity();
    }

    private loadTransportistas(): void {
        if (this.transportistasLoaded || this.isLoadingTransportistas) return;
        this.isLoadingTransportistas = true;
        this.transportistasLoadError = null;
        this.userService.getTransportistas()
            .pipe(finalize(() => { this.isLoadingTransportistas = false; }))
            .subscribe({
                next: data => {
                    this.transportistas = data;
                    this.transportistasLoaded = true;
                },
                error: error => {
                    this.logger.error('Error al cargar transportistas', error as Error, 'CreateUserPageComponent');
                    this.transportistasLoadError =
                        'No se pudo cargar la lista de transportistas. Recargue la página para reintentar.';
                },
            });
    }

    shouldShowConsigneeSelect(): boolean {
        return this.userForm.get('role')?.value === 'consignatario';
    }

    private loadConsignees(): void {
        this.isLoadingConsignees = true;
        this.controlDusService.getConsignees()
            .pipe(
                catchError(() => of([])),
                finalize(() => { this.isLoadingConsignees = false; })
            )
            .subscribe(data => { this.consignees = data; });
    }

    passwordMatchValidator(group: FormGroup): { [key: string]: any } | null {
        const password = group.get('password')?.value;
        const confirmPassword = group.get('confirmPassword')?.value;
        
        if (password && confirmPassword && password !== confirmPassword) {
            return { passwordMismatch: true };
        }
        return null;
    }

    getErrorMessage(fieldName: string): string {
        const control = this.userForm.get(fieldName);
        if (!control) return '';

        if (control.hasError('required')) {
            return 'Este campo es requerido';
        }

        if (control.hasError('email')) {
            return 'Ingrese un email válido';
        }

        if (control.hasError('minlength')) {
            const requiredLength = control.errors?.['minlength']?.requiredLength;
            return `Mínimo ${requiredLength} caracteres`;
        }

        if (control.hasError('pattern')) {
            return 'La contraseña debe contener al menos una mayúscula, un número y un carácter especial';
        }

        if (control.hasError('passwordMismatch')) {
            return 'Las contraseñas no coinciden';
        }

        return 'Campo inválido';
    }

    markFormGroupTouched(): void {
        Object.keys(this.userForm.controls).forEach(key => {
            const control = this.userForm.get(key);
            control?.markAsTouched();
        });
    }

    onSubmit(): void {
        // Sin la lista no se puede elegir ni validar el transportista (REQ-FE-4.5)
        if (this.shouldShowTransportistaSelect() && this.transportistasLoadError) {
            this.snackBar.open(this.transportistasLoadError, 'Cerrar', {
                duration: 5000,
                panelClass: ['error-snackbar']
            });
            return;
        }
        if (this.userForm.valid) {
            this.isLoading = true;
            
            const formData = this.userForm.value;
            const userData: CreateUserRequest = {
                email: formData.email,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone: formData.phone,
                role: formData.role,
                client_id: formData.clientId,
                consignee_id: formData.consigneeId,
                ...(formData.role === 'transportista' ? { transportista_id: formData.transportistaId } : {}),
                password: formData.password,
                is_active: formData.isActive
            };

            this.userService.createUser(userData)
                .pipe(
                    catchError(error => {
                        this.logger.error('Error al crear usuario', error as Error, 'CreateUserPageComponent');
                        const errorMessage = error.error?.message || 'Error al crear el usuario';
                        this.snackBar.open(errorMessage, 'Cerrar', {
                            duration: 5000,
                            panelClass: ['error-snackbar']
                        });
                        return of(null);
                    }),
                    finalize(() => {
                        this.isLoading = false;
                    })
                )
                .subscribe(response => {
                    if (response && response.success && response.data) {
                        // Asignar módulos si hay alguno seleccionado
                        if (this.selectedModules.length > 0) {
                            this.userService.assignModules(response.data.id, this.selectedModules)
                                .pipe(
                                    catchError(error => {
                                        this.logger.error('Error al asignar módulos', error as Error, 'CreateUserPageComponent');
                                        this.snackBar.open('Usuario creado pero hubo un error al asignar módulos', 'Cerrar', {
                                            duration: 5000,
                                            panelClass: ['warning-snackbar']
                                        });
                                        return of(null);
                                    })
                                )
                                .subscribe(modulesResponse => {
                                    if (modulesResponse && modulesResponse.success) {
                                        this.snackBar.open('Usuario creado y módulos asignados exitosamente', 'Cerrar', {
                                            duration: 3000,
                                            panelClass: ['success-snackbar']
                                        });
                                    }
                                    this.router.navigate(['/users']);
                                });
                        } else {
                            this.snackBar.open('Usuario creado exitosamente', 'Cerrar', {
                                duration: 3000,
                                panelClass: ['success-snackbar']
                            });
                            this.router.navigate(['/users']);
                        }
                    }
                });
        } else {
            this.markFormGroupTouched();
        }
    }

    onCancel(): void {
        this.router.navigate(['/users']);
    }

    // Métodos de validación de contraseña para indicadores visuales
    hasUpperCase(password: string | null | undefined): boolean {
        return !!password && /[A-Z]/.test(password);
    }

    hasNumber(password: string | null | undefined): boolean {
        return !!password && /\d/.test(password);
    }

    hasSpecialChar(password: string | null | undefined): boolean {
        return !!password && /[@$!%*?&#]/.test(password);
    }
} 